// Companion/blocks/chatBlockRegistry.tsx
//
// The block registry: one entry per `block.kind`, each pairing the tolerant
// payload parser with the component that renders it. Everything the thread
// knows about served content it learns here — `resolveChatBlock` looks a kind
// up, and a kind with no entry, or a payload that will not parse, resolves to
// null so the caller can draw a placeholder instead of crashing or leaving a
// hole in the answer.
//
// Adding a serve tool is one entry plus one adapter; nothing in the thread, the
// slice, or the stream client changes.

import type { ReactNode } from 'react';
import type { ChatBlock } from '@/slices/chat/chatSlice';
import type { ChatSurface } from '@/types/chatEnvelope';
import { isChatBlockKind, type ChatBlockKind } from '@/types/chatBlocks';
import ArticleBlock from './ArticleBlock';
import DashboardBlock from './DashboardBlock';
import DocumentBlock from './DocumentBlock';
import DocumentExcerptBlock from './DocumentExcerptBlock';
import DraftQuestionBlock from './DraftQuestionBlock';
import FileBlock from './FileBlock';
import FlashcardsBlock from './FlashcardsBlock';
import HelpDocBlock from './HelpDocBlock';
import QuestionBlock from './QuestionBlock';
import SetResultBlock from './SetResultBlock';
import StudySetBlock from './StudySetBlock';
import { studySetTitle } from './StudySetHeader';
import VideoBlock from './VideoBlock';
import NavigationBlock from './NavigationBlock';
import type { ChatQuestionFollowup } from './chatQuestionFollowup';
import {
  parseArticleBlockPayload,
  parseDashboardBlockPayload,
  parseDocumentBlockPayload,
  parseDocumentExcerptBlockPayload,
  parseDraftQuestionBlockPayload,
  parseFileBlockPayload,
  parseFlashcardsBlockPayload,
  parseHelpDocBlockPayload,
  parseQuestionBlockPayload,
  parseSetResultBlockPayload,
  parseStudySetBlockPayload,
  parseVideoBlockPayload,
  parseNavigationBlockPayload,
} from './chatBlockPayload';
import McGrawHillActions, {
  parseMcGrawHillActionsPayload,
} from '../../../../../mcgrawHill/McGrawHillActions'; // mcgraw-hill
import McGrawHillFigureCard, {
  parseMcGrawHillFigurePayload,
} from '../../../../../mcgrawHill/McGrawHillFigureCard'; // mcgraw-hill

/** What a renderer is told about where and how it is being drawn. */
export interface ChatBlockRenderContext {
  /** Which shell the block is in; the video block reads it. */
  surface: ChatSurface;
  /**
   * True for a block restored from a legacy conversation. Those are an archive
   * of what the old pipeline served: nothing in them may be answered, flipped,
   * or re-submitted.
   */
  isReadOnly: boolean;
  /** Reports whether an interactive question is currently answered. */
  onAnsweredChange?: (
    answered: boolean,
    question?: ChatQuestionFollowup
  ) => void;
  /** Ask the thread to preserve its position across a block disclosure. */
  onBeforeContentExpand?: () => void;
}

/**
 * The render context plus the block it describes. `resolveChatBlock` adds it,
 * so a renderer that needs to name its own block on the wire — the question
 * block's badges add content to the thread by referring to it (ORA-546/547) —
 * has its ids without every caller having to pass them.
 */
export interface ChatBlockRenderScope extends ChatBlockRenderContext {
  block: ChatBlock;
}

/** A block that resolved: a heading and the content under it. */
export interface ResolvedChatBlock {
  title: string;
  /**
   * True when the title names the KIND rather than the content it holds —
   * "Practice Question", "Flashcards". The header draws one label for those
   * instead of repeating itself ("QUESTION  Practice question", ORA-628); a
   * block with a title of its own keeps both.
   */
  isGenericTitle: boolean;
  /**
   * True where the renderer draws the header row itself, so `ChatBlocks` skips
   * its own. A study set needs state on the title line — where the student is
   * in it — and a band of its own above the content is what that looked like
   * before (ORA-685). The title is still resolved: the thread reads it.
   */
  ownsHeader: boolean;
  body: ReactNode;
}

interface ChatBlockEntry {
  resolve: (
    payload: Record<string, unknown>,
    scope: ChatBlockRenderScope
  ) => ResolvedChatBlock | null;
}

/**
 * One registry entry. The generic is inferred from `parse`, so `title` and
 * `render` see the parsed payload's real type and the registry needs no casts.
 */
const entry = <TPayload,>(definition: {
  parse: (payload: Record<string, unknown>) => TPayload | null;
  title: (payload: TPayload) => string;
  /** Set it where the title is the kind's own name; see `ResolvedChatBlock`. */
  genericTitle?: true;
  /** Set it where the renderer draws its own header; see `ResolvedChatBlock`. */
  ownsHeader?: true;
  render: (payload: TPayload, scope: ChatBlockRenderScope) => ReactNode;
}): ChatBlockEntry => ({
  resolve: (payload, scope) => {
    const parsed = definition.parse(payload);
    if (parsed === null) return null;

    return {
      title: definition.title(parsed),
      isGenericTitle: definition.genericTitle === true,
      ownsHeader: definition.ownsHeader === true,
      body: definition.render(parsed, scope),
    };
  },
});

const REGISTRY: Record<ChatBlockKind, ChatBlockEntry> = {
  question: entry({
    parse: parseQuestionBlockPayload,
    title: () => 'Practice Question',
    genericTitle: true,
    render: (
      payload,
      { block, isReadOnly, onAnsweredChange, onBeforeContentExpand }
    ) => (
      <QuestionBlock
        payload={payload}
        block={block}
        isReadOnly={isReadOnly}
        onAnsweredChange={onAnsweredChange}
        onBeforeContentExpand={onBeforeContentExpand}
      />
    ),
  }),
  flashcards: entry({
    parse: parseFlashcardsBlockPayload,
    title: (payload) =>
      payload.cards.length === 1 ? 'Flashcard' : 'Flashcards',
    genericTitle: true,
    render: (payload, { isReadOnly }) => (
      <FlashcardsBlock payload={payload} isReadOnly={isReadOnly} />
    ),
  }),
  video: entry({
    parse: parseVideoBlockPayload,
    title: (payload) => payload.title || 'Video',
    render: (payload, { surface, isReadOnly }) => (
      <VideoBlock payload={payload} surface={surface} isReadOnly={isReadOnly} />
    ),
  }),
  article: entry({
    parse: parseArticleBlockPayload,
    title: (payload) => payload.title || 'Article',
    render: (payload) => <ArticleBlock payload={payload} />,
  }),
  help_doc: entry({
    parse: parseHelpDocBlockPayload,
    title: (payload) => payload.title || 'Help Doc',
    render: (payload) => <HelpDocBlock payload={payload} />,
  }),
  dashboard: entry({
    parse: parseDashboardBlockPayload,
    title: () => 'Performance Data',
    genericTitle: true,
    render: (payload) => <DashboardBlock payload={payload} />,
  }),
  document: entry({
    parse: parseDocumentBlockPayload,
    title: (payload) => payload.fileName || 'Document',
    render: (payload) => <DocumentBlock payload={payload} />,
  }),
  // mcgraw-hill: a scripted answer's action row.
  mcgraw_hill_actions: entry({
    parse: parseMcGrawHillActionsPayload,
    title: () => 'Actions',
    ownsHeader: true,
    render: (payload) => <McGrawHillActions payload={payload} />,
  }),
  // mcgraw-hill: a reference figure placed by a scripted answer.
  mcgraw_hill_figure: entry({
    parse: parseMcGrawHillFigurePayload,
    title: (payload) => payload.label,
    ownsHeader: true,
    render: (payload) => <McGrawHillFigureCard payload={payload} />,
  }),
  document_excerpt: entry({
    parse: parseDocumentExcerptBlockPayload,
    title: (payload) =>
      payload.page
        ? `${payload.title || 'Document'} · p. ${payload.page}`
        : payload.title || 'Document',
    render: (payload) => <DocumentExcerptBlock payload={payload} />,
  }),
  study_set: entry({
    parse: parseStudySetBlockPayload,
    title: studySetTitle,
    // The set's header carries its progress, so it draws the row itself.
    ownsHeader: true,
    render: (
      payload,
      { block, surface, isReadOnly, onAnsweredChange, onBeforeContentExpand }
    ) => (
      <StudySetBlock
        payload={payload}
        block={block}
        surface={surface}
        isReadOnly={isReadOnly}
        onAnsweredChange={onAnsweredChange}
        onBeforeContentExpand={onBeforeContentExpand}
      />
    ),
  }),
  set_result: entry({
    parse: parseSetResultBlockPayload,
    title: () => 'Study Set',
    genericTitle: true,
    // Drawn as a bare line with no header at all; see `SetResultBlock`.
    render: (payload) => <SetResultBlock payload={payload} />,
  }),
  draft_question: entry({
    parse: parseDraftQuestionBlockPayload,
    // The label a draft keeps in the thread and on history reload: the kind it
    // is, plus what makes it different from the vetted one above it.
    title: () => 'Practice Question · AI draft',
    genericTitle: true,
    render: (payload, { isReadOnly, onBeforeContentExpand }) => (
      <DraftQuestionBlock
        payload={payload}
        isReadOnly={isReadOnly}
        onBeforeContentExpand={onBeforeContentExpand}
      />
    ),
  }),
  file: entry({
    parse: parseFileBlockPayload,
    title: (payload) => payload.title || payload.filename,
    ownsHeader: true,
    render: (payload) => <FileBlock payload={payload} />,
  }),
  navigation: entry({
    parse: parseNavigationBlockPayload,
    title: () => 'Go To',
    // Drawn without a header at all, but the flag is still true of the title.
    genericTitle: true,
    render: (payload, { block }) => (
      <NavigationBlock block={block} payload={payload} />
    ),
  }),
};

/**
 * Resolve a block from the slice, or null when this client does not know the
 * kind (a newer backend serving something we cannot draw) or the payload will
 * not parse. Callers render a placeholder for null; they never read the payload
 * themselves.
 */
export const resolveChatBlock = (
  block: ChatBlock,
  context: ChatBlockRenderContext
): ResolvedChatBlock | null =>
  isChatBlockKind(block.kind)
    ? REGISTRY[block.kind].resolve(block.payload, { ...context, block })
    : null;
